import { getDevice } from '@/utils'
import { useMemo } from 'react'
import { defineStore } from '@/utils/store'

const DefaultScreen = { sm: 576, md: 1024, lg: 1440, xl: 1920, ['2xl']: 1920 }

export type WebsiteState = {
  // 專案字體大小
  fontSize: number
  // 版型的響應集距，需要在版型中配置
  screens: Record<'sm' | 'md' | 'lg' | 'xl' | '2xl', number>
  // 視窗寬高
  size: {
    // 視窗寬
    width: number
    // 視窗高
    height: number
  }
  // 暫時跳過 reFontSize
  skipReFontSize: boolean
  // 安全範圍
  safeArea: {
    top: number
    right: number
    bottom: number
    left: number
  }
  // 是否初始化
  init: boolean
  // 被嵌入狀態
  isWebview: boolean
  // 三方模式
  isThirdMode: boolean
  // 老客戶
  isOldUser: boolean
  // 當前裝置是否為手機
  isMobile: boolean
  isMobileScreen: boolean // 這個是指版型，不是 device
  // 簡易版型：給純頁面使用，不初始化用戶等其他初始行為
  isSimplePage: boolean
  // 網站訪問次數
  visitCount: number
}

/** 存放當前網站相關資訊 */
export const [useWebsiteStore, setWebsiteState] = defineStore<WebsiteState>(
  {
    // 專案字體大小
    fontSize: window.innerWidth < (window.__screens?.sm! || 0) ? window.innerWidth / window.__screens?.sm! : 1,
    // RWD 版型的響應集距
    screens: window.__screens || DefaultScreen,
    // 視窗寬高
    size: {
      width: window.innerWidth ?? 0,
      height: window.innerHeight ?? 0,
    },
    // 暫時跳過 reFontSize
    skipReFontSize: false,
    // 安全範圍
    safeArea: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    init: false,
    isMobile: getDevice() !== 'PC',
    isMobileScreen: false,
    isWebview: false,
    isThirdMode: false,
    isOldUser: false,
    isSimplePage: false,
    visitCount: 0,
  },
  {
    name: 'WEBSITE',
    partialize: (state) => ({ isWebview: state.isWebview, visitCount: state.visitCount }) as WebsiteState,
  },
)

/**
 * 判斷視窗大小用
 * @example
 * const { is, lt, gt, breakpoints } = useRWD()
 * is.sm // is.md, is.lg is.lt_md is.gt_md
 * breakpoints({ 0: A, 500: B, 1000: C }) // 條件大於時返回指定物件, 未設定 0 的時候，< 500 識別為 B
 */
export const useRWD = () => {
  const isMobileScreen = useWebsiteStore((state) => state.isMobileScreen)
  const init = useWebsiteStore((state) => state.init)
  // Size 有 debounce 不會 resize 就更新
  const size = useWebsiteStore((state) => state.size)

  return useMemo(() => {
    // Static value
    const { screens, fontSize, size } = useWebsiteStore.getState()
    const { width, height } = size

    /** 計算等比單位用 */
    const rem = (n: number) => n * fontSize

    /** 計算是否大於 sm md lg xl */
    type Key = keyof WebsiteState['screens'] | `lt_${keyof WebsiteState['screens']}` | `gt_${keyof WebsiteState['screens']}`
    const lt = (n: number) => width < rem(n)
    const gt = (n: number) => width > rem(n)
    const is = new Proxy({} as Record<Key, boolean>, {
      get: function (_: Record<Key, boolean>, key: string) {
        if (!init) return true // 讓 Server 階段也渲染
        const type = String(key).toLowerCase() as Key
        // 小於
        if (type.startsWith('lt_')) {
          const n = screens[type.replace('lt_', '') as keyof typeof screens]
          return width < n
        }
        // 大於
        if (type.startsWith('gt_')) {
          const n = screens[type.replace('gt_', '') as keyof typeof screens]
          return width >= n
        }
        return false
      },
    })

    /**
     * 類似 Swiper 的 breakpoints
     * breakpoints({ 0: A, 500: B, 1000: C }) // 條件大於時返回指定物件, 未設定 0 的時候，< 500 識別為 B
     */
    const breakpoints = <T = any>(condition: Record<number | string, T>) => {
      const sizes = Object.keys(condition)
        .map(Number)
        .sort((a, b) => b - a)
      for (let i = 0; i < sizes.length; i++) {
        const size = sizes[i]
        if (width >= Number(size)) return condition[size]
      }
      return condition[sizes?.[sizes.length - 1]]
    }

    return {
      screens,
      // rem to px
      rem,
      // is.sm || is.md || is.lg
      is,
      isMobileScreen,
      // 是否小於視窗，注意數值會轉 rem
      lt,
      // 是否大於視窗，注意數值會轉 rem
      gt,
      // RWD 區間判斷
      breakpoints,
      // 螢幕尺寸
      size,
    }
  }, [init, isMobileScreen, size])
}

/**
 * 判斷是否小於指定寬度
 * @return {boolean} 是否小於指定寬度，預設「是否返回小於 md 版型」
 */
export const compareScreen = (responsive: keyof WebsiteState['screens'] | number = 'md') => {
  const { screens, size } = useWebsiteStore.getState()
  if (typeof responsive === 'string') return size.width < screens[responsive]
  return size.width < responsive
}
